/**
 * Adapted KPI component.
 *
 * Data sourcing strategy:
 * - Always renders with inline data (value/label/unit) initially
 * - When inputDataset is present AND data is available, uses fetched value
 * - Label and unit from inline data are preserved even when using fetched value
 */

import { Kpi } from '#core/components/dashboard/panels/kpi/Kpi.component.tsx';
import type { KpiSchemaOutput } from '#core/components/dashboard/panels/kpi/schema.ts';

import type { ChatDashboardItem } from '#plugin-chat/components/dashboard/types.ts';
import { createKpiAdapter } from '#plugin-chat/components/tools/show-dashboard/helpers.ts';
import { useDataset } from '#plugin-chat/hooks/useDataset.ts';

export interface AdaptedKpiProps {
  item: ChatDashboardItem;
}

export function AdaptedKpi({ item }: AdaptedKpiProps) {
  const component = item.component as Partial<KpiSchemaOutput>;

  const dataset = useDataset(item.inputDataset);
  const fetchedData = dataset ? createKpiAdapter(dataset) : undefined;

  // Determine the value to display:
  // - Use fetched value if inputDataset is present AND data is available
  // - Otherwise fall back to inline value
  const rawValue = component.value;

  // Detect formula-like strings the LLM may hallucinate (e.g. "sum(col)")
  const value = typeof rawValue === 'string' && /^\w+\(.*\)$/.test(rawValue.trim()) ? undefined : rawValue;

  // Label and unit: prefer inline (from component), fall back to fetched
  const label = component.label ?? fetchedData?.label ?? 'No label';
  const unit = component.unit ?? fetchedData?.unit;

  // No value available yet (waiting for data)
  if (value === undefined) {
    return <Kpi label={label} unit={unit} value="—" />;
  }

  return (
    <Kpi
      color={component.color}
      decimalScale={component.decimalScale}
      kind={component.kind}
      label={label}
      trend={component.trend}
      trendValue={component.trendValue}
      unit={unit}
      value={value}
    />
  );
}
